import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { MultiSelect, type Option } from '../../../components/ui/multi-select';
import { Pagination } from '../../../components/ui/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { CollapsibleFilterBar } from '../../project/components/dashboard/materials/tabs/Filters/CollapsibleFilterBar';
import { cn } from '../../../lib/utils';
import type { ReportHistoryPipeRow } from '../api/reportHistory';

interface PipeDataReportDetailsTableProps {
  pipes: ReportHistoryPipeRow[];
  hasInnerRadiusData?: boolean;
  hasOuterRadiusData?: boolean;
}

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

type SortKey =
  | 'pipeEndID'
  | 'minID'
  | 'avgID'
  | 'maxID'
  | 'ooR'
  | 'minOD'
  | 'avgOD'
  | 'maxOD'
  | 'ooROD';

type SortDir = 'asc' | 'desc';

function formatMm(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(2);
}

function outOfSpecClass(apiClass?: string): string {
  return apiClass === 'pipe-value-out-of-spec' ? 'text-readable-red font-semibold' : '';
}

function clampPageSize(n: number): number {
  return Math.min(1000, Math.max(1, Math.floor(n)));
}

function compareNullableNumber(a: number | null | undefined, b: number | null | undefined): number {
  const aMissing = a == null || !Number.isFinite(a);
  const bMissing = b == null || !Number.isFinite(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return (a as number) - (b as number);
}

function SortableHead({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className,
  align = 'left',
  rowSpan,
}: {
  label: string;
  sortKey: SortKey;
  activeKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  className?: string;
  align?: 'left' | 'center';
  rowSpan?: number;
}) {
  const active = activeKey === sortKey;
  return (
    <TableHead
      rowSpan={rowSpan}
      className={cn(align === 'center' && 'text-center', className)}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 font-semibold hover:text-blueprint-blue transition-colors',
          align === 'center' && 'justify-center w-full',
          active ? 'text-blueprint-blue' : 'text-inherit'
        )}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" aria-hidden />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" aria-hidden />
        )}
      </button>
    </TableHead>
  );
}

export const PipeDataReportDetailsTable: React.FC<PipeDataReportDetailsTableProps> = ({
  pipes,
  hasInnerRadiusData = true,
  hasOuterRadiusData = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [pipeIdFilter, setPipeIdFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('pipeEndID');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const itemsPerPageRef = useRef(itemsPerPage);
  itemsPerPageRef.current = itemsPerPage;

  const pipeIdOptions: Option[] = useMemo(() => {
    const ids = Array.from(
      new Set(pipes.map((p) => String(p.pipeEndID || '').trim()).filter(Boolean))
    );
    ids.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return ids.map((id) => ({ value: id, label: id }));
  }, [pipes]);

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery('');
    setPipeIdFilter([]);
    setSortKey('pipeEndID');
    setSortDir('asc');
  }, [pipes]);

  const filteredPipes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const selected = pipeIdFilter.length > 0 ? new Set(pipeIdFilter) : null;
    return pipes.filter((p) => {
      const endId = String(p.pipeEndID || '').trim();
      const matchesSearch = !q || endId.toLowerCase().includes(q);
      const matchesDropdown = !selected || selected.has(endId);
      return matchesSearch && matchesDropdown;
    });
  }, [pipes, searchQuery, pipeIdFilter]);

  const hasActiveFilters = Boolean(searchQuery.trim()) || pipeIdFilter.length > 0;

  const sortedPipes = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filteredPipes].sort((a, b) => {
      if (sortKey === 'pipeEndID') {
        return (
          dir *
          String(a.pipeEndID ?? '').localeCompare(String(b.pipeEndID ?? ''), undefined, {
            numeric: true,
          })
        );
      }
      return dir * compareNullableNumber(a[sortKey], b[sortKey]);
    });
  }, [filteredPipes, sortKey, sortDir]);

  const totalItems = sortedPipes.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, pipeIdFilter, sortKey, sortDir]);

  const paginatedPipes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedPipes.slice(start, start + itemsPerPage);
  }, [sortedPipes, currentPage, itemsPerPage]);

  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);
  const rangeLabel =
    totalItems > 0 ? `Showing ${rangeStart}–${rangeEnd} of ${totalItems}` : 'No rows';

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  if (!pipes.length) {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-sm text-heritage-grey text-center">
            No pipe profile data available for this report.
          </p>
        </CardContent>
      </Card>
    );
  }

  const showId = hasInnerRadiusData || pipes.some((p) => p.minID != null || p.avgID != null || p.maxID != null);
  const showOd =
    hasOuterRadiusData || pipes.some((p) => p.minOD != null || p.avgOD != null || p.maxOD != null);

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <CollapsibleFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by pipe ID…"
          activeFilterCount={hasActiveFilters ? Math.max(pipeIdFilter.length, 1) : 0}
          filteredCount={totalItems}
          filteredCountLabel="pipes"
          onResetFilters={() => {
            setSearchQuery('');
            setPipeIdFilter([]);
          }}
        >
          <MultiSelect
            options={pipeIdOptions}
            selected={pipeIdFilter}
            onChange={setPipeIdFilter}
            placeholder="Pipe ID"
            className="w-full"
            isSearchable
          />
        </CollapsibleFilterBar>
        <div className="text-[12px] text-heritage-grey">
          <span>{rangeLabel}</span>
        </div>

        <div className="overflow-x-auto">
          <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
            <Table
              className="min-w-[880px] table-fixed"
              containerClassName="relative w-full overflow-visible"
            >
              <colgroup>
                <col className="w-[72px]" />
                <col className="w-[240px]" />
                {showId ? (
                  <>
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                  </>
                ) : null}
                {showOd ? (
                  <>
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                    <col className="w-[110px]" />
                  </>
                ) : null}
              </colgroup>
              <TableHeader className="sticky top-0 z-20 bg-white">
                <TableRow className="hover:bg-transparent">
                  <TableHead rowSpan={2} className="w-[72px] text-center align-middle">
                    SL No
                  </TableHead>
                  <SortableHead
                    label="Pipe Profile Reference"
                    sortKey="pipeEndID"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={handleSort}
                    rowSpan={2}
                    className="align-middle"
                  />
                  {showId && (
                    <TableHead colSpan={4} className="text-center border-b border-[#E2E5EA]">
                      ID
                    </TableHead>
                  )}
                  {showOd && (
                    <TableHead colSpan={4} className="text-center border-b border-[#E2E5EA]">
                      OD
                    </TableHead>
                  )}
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  {showId && (
                    <>
                      <SortableHead label="Min(mm)" sortKey="minID" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="Avg(mm)" sortKey="avgID" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="Max(mm)" sortKey="maxID" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="OoR(mm)" sortKey="ooR" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                    </>
                  )}
                  {showOd && (
                    <>
                      <SortableHead label="Min(mm)" sortKey="minOD" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="Avg(mm)" sortKey="avgOD" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="Max(mm)" sortKey="maxOD" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                      <SortableHead label="OoR(mm)" sortKey="ooROD" activeKey={sortKey} dir={sortDir} onSort={handleSort} align="center" />
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPipes.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2 + (showId ? 4 : 0) + (showOd ? 4 : 0)}
                      className="text-center text-heritage-grey py-8"
                    >
                      No pipes match your search or filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPipes.map((row, index) => {
                    const slNo = (currentPage - 1) * itemsPerPage + index + 1;
                    return (
                      <TableRow key={row.pipeEndID || `${slNo}`}>
                        <TableCell className="text-center tabular-nums">{slNo}</TableCell>
                        <TableCell className="font-medium text-foreground whitespace-nowrap truncate">
                          {row.pipeEndID ?? '—'}
                        </TableCell>
                        {showId && (
                          <>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.minIDClass))}>
                              {formatMm(row.minID)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.avgIDClass))}>
                              {formatMm(row.avgID)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.maxIDClass))}>
                              {formatMm(row.maxID)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.ooRClass))}>
                              {formatMm(row.ooR)}
                            </TableCell>
                          </>
                        )}
                        {showOd && (
                          <>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.minODClass))}>
                              {formatMm(row.minOD)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.avgODClass))}>
                              {formatMm(row.avgOD)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.maxODClass))}>
                              {formatMm(row.maxOD)}
                            </TableCell>
                            <TableCell className={cn('text-center tabular-nums', outOfSpecClass(row.ooRODClass))}>
                              {formatMm(row.ooROD)}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => {
            const lim = clampPageSize(n);
            itemsPerPageRef.current = lim;
            setItemsPerPage(lim);
            setCurrentPage(1);
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          showPageNumbers
          maxVisiblePages={5}
        />
      </CardContent>
    </Card>
  );
};
